import { useState, useContext, useMemo, useEffect, useCallback } from "react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { Button } from "@/components/ui/button";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useI18n } from "@/hooks/useI18n";
import { Calendar, Edit3, CheckCircle, CreditCard, Activity, Plus } from "lucide-react";

import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TabsHeader } from "@/components/ui/tabs-header";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { useDashboardItineraryCounts, useDashboardTravelRequest } from "@/hooks/useDashboardLists";
import { TravelRequestModal } from "@/lib/requests";
import { toast } from "@/hooks/useToast";
import { useItineraries } from "@/hooks/useItineraries";

import { DashboardItinerariesTab } from "@/lib/dashboard/DashboardItinerariesTab";
import { CreateItineraryFromRequestModal } from "@/lib/dashboard/CreateItineraryFromRequestModal";
import { ItineraryTabConfig } from "@/lib/dashboard/types";

const Dashboard = () => {
  const { t } = useI18n();

  // Pagina di soli itinerari: le richieste di viaggio vivono in /requests e non
  // servono nemmeno per le card, che rimandano lì con un link. Qui si leggono solo i
  // contatori dei badge; ogni tab chiede da sola la propria pagina.
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshData = useCallback(() => setRefreshKey((k) => k + 1), []);
  const { data: counts } = useDashboardItineraryCounts({}, refreshKey);
  const countOf = (tab: ItineraryTabConfig["value"]) => counts.byTab[tab] ?? 0;

  const { deleteItinerary, duplicateItinerary, updateItineraryStatus } =
    useItineraries();

  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error("Dashboard must be used inside an AuthProvider");
  const userRole = authContext.userRole;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSidebarCollapsed } = useSidebar();
  const activeTab = searchParams.get("tab") || (userRole === "traveler" ? "proposals" : "draft");

  // Le mail già spedite puntano a /dashboard?tab=requests&requestId=…: le
  // richieste ora hanno una pagina propria, quindi quei link vanno inoltrati.
  const legacyRequestId = searchParams.get("requestId");
  const wantsRequestsTab = searchParams.get("tab") === "requests";

  // Un itinerario parte sempre da una richiesta aperta: la si sceglie qui.
  const [isCreateItineraryModalOpen, setIsCreateItineraryModalOpen] = useState(false);

  // Il viaggiatore legge i propri viaggi, non li costruisce.
  const canCreateItinerary = userRole !== "traveler";

  const handleUpdateItineraryStatus = async (id: string, status: any) => {
    const result = await updateItineraryStatus(id, status);
    refreshData();
    return result;
  };

  // "Vedi richiesta" da un itinerario: il dettaglio si apre qui sopra, senza portare
  // l'utente sulla pagina Richieste. La richiesta si legge per id.
  const [viewRequestId, setViewRequestId] = useState<string | null>(null);
  const { data: requestLink, loading: requestLoading } = useDashboardTravelRequest(viewRequestId);
  const viewedRequest =
    viewRequestId && requestLink.resolvedId === viewRequestId ? requestLink.request : null;
  const handleViewTravelRequest = (id: string) => setViewRequestId(id);

  // Non trovata o non visibile con i permessi correnti: meglio dirlo che non aprire nulla.
  useEffect(() => {
    if (!viewRequestId || requestLoading || requestLink.resolvedId !== viewRequestId) return;
    if (!requestLink.request) {
      toast({
        title: String(t("dashboard.requestNotFoundTitle")),
        description: String(t("dashboard.requestNotFoundDesc")),
        variant: "destructive",
      });
      setViewRequestId(null);
    }
  }, [viewRequestId, requestLoading, requestLink, t]);

  // ── Itinerary action handlers ────────────────────────────────────────────
  const handleEditItinerary = (id: string) =>
    navigate(`/itinerary-builder?itineraryId=${id}`);

  const handleDeleteItinerary = async (id: string) => {
    const success = await deleteItinerary(id);
    if (success) refreshData();
  };

  const handleDuplicateItinerary = async (id: string) => {
    await duplicateItinerary(id);
    refreshData();
  };

  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  const handleCreateItineraryFromRequest = (request: DashboardTravelRequest) => {
    setIsCreateItineraryModalOpen(false);
    navigate(`/itinerary-builder?requestId=${request.id}`);
  };

  useEffect(() => {
    if (!wantsRequestsTab && !legacyRequestId) {
      return;
    }
    navigate(
      legacyRequestId ? `/requests?requestId=${legacyRequestId}` : "/requests",
      { replace: true }
    );
  }, [wantsRequestsTab, legacyRequestId, navigate]);

  // ── Tab header config (role-aware, drives badge counts) ──────────────────
  const tabs = useMemo(() => {
    if (userRole === "traveler") {
      return [
        { value: "planned", label: t("dashboard.tabPlanned"), icon: Calendar, badge: countOf("planned").toString() },
        { value: "proposals", label: t("dashboard.tabProposals"), icon: CreditCard, badge: countOf("proposals").toString() },
        { value: "finished", label: t("dashboard.tabFinished"), icon: CheckCircle, badge: countOf("finished").toString() },
      ];
    }
    return [
      { value: "draft", label: t("dashboard.tabDrafts"), icon: Edit3, badge: countOf("draft").toString() },
      { value: "active", label: t("dashboard.tabActive"), icon: Activity, badge: countOf("active").toString() },
      { value: "planned", label: t("dashboard.tabPlanned"), icon: Calendar, badge: countOf("planned").toString() },
      { value: "finished", label: t("dashboard.tabFinished"), icon: CheckCircle, badge: countOf("finished").toString() },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, t, counts]);

  // ── Itinerary tab configs (pure data – no state setters) ─────────────────
  const itineraryTabConfigs: ItineraryTabConfig[] = useMemo(() => {
    const all: ItineraryTabConfig[] = [
      {
        value: "draft",
        labelKey: "dashboard.tabDrafts",
        icon: Edit3,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "agency"],
        showDesignerFilter: true,
        // Niente "tutti": gli archiviati non sono lavoro aperto, non vanno fra le bozze.
        phases: [
          { value: "drafts", labelKey: "dashboard.phaseDrafts", tone: "draft" },
          { value: "archived", labelKey: "dashboard.phaseArchived", tone: "archived", emptyTitleKey: "dashboard.noArchivedItineraries" },
        ],
        i18n: {
          emptyTitleKey: "dashboard.noDraftItinerariesYet",
          emptyTitleDesignerKey: "dashboard.noDraftItinerariesForDesigner",
          emptyDescKey: "dashboard.createFirstItinerary",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        value: "proposals",
        labelKey: "dashboard.tabProposals",
        icon: CreditCard,
        visibleTo: ["traveler"],
        showDesignerFilter: false,
        phases: [
          { value: "all", labelKey: "dashboard.phaseAll", tone: "neutral" },
          { value: "sent", labelKey: "dashboard.phaseToReview", tone: "sent" },
          { value: "changes", labelKey: "dashboard.phaseChanges", tone: "changes" },
        ],
        i18n: {
          emptyTitleKey: "dashboard.noProposalsItineraries",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        value: "active",
        labelKey: "dashboard.tabActive",
        icon: Activity,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "agency"],
        showDesignerFilter: true,
        phases: [
          { value: "all", labelKey: "dashboard.phaseAll", tone: "neutral" },
          { value: "sent", labelKey: "dashboard.phaseSent", tone: "sent" },
          { value: "changes", labelKey: "dashboard.phaseChanges", tone: "changes" },
          { value: "installments", labelKey: "dashboard.phaseInstallments", tone: "installments" },
        ],
        i18n: {
          emptyTitleKey: "dashboard.noActiveItineraries",
          emptyTitleDesignerKey: "dashboard.noActiveItinerariesForDesigner",
          emptyDescKey: "dashboard.activeItinerariesDesc",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        value: "planned",
        labelKey: "dashboard.tabPlanned",
        icon: Calendar,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "traveler", "agency"],
        showDesignerFilter: true,
        phases: [
          { value: "all", labelKey: "dashboard.phaseAll", tone: "neutral" },
          { value: "upcoming", labelKey: "dashboard.phaseUpcoming", tone: "confirmed" },
          { value: "ongoing", labelKey: "dashboard.phaseOngoing", tone: "confirmed" },
        ],
        i18n: {
          emptyTitleKey: "dashboard.noPlannedItineraries",
          emptyTitleDesignerKey: "dashboard.noItinerariesForDesigner",
          emptyDescKey: "dashboard.completeFirstItinerary",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadCompletedError",
        },
      },
      {
        value: "finished",
        labelKey: "dashboard.tabFinished",
        icon: CheckCircle,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "traveler", "agency"],
        showDesignerFilter: true,
        i18n: {
          emptyTitleKey: "dashboard.noFinishedItineraries",
          emptyTitleDesignerKey: "dashboard.noItinerariesForDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
    ];
    return all.filter((cfg) => cfg.visibleTo.includes(userRole ?? ""));
  }, [userRole]);

  return (
    <PrivateLayout
      pageTitle={t("dashboard.pageTitle")}
      pageDescription={t("dashboard.pageDescription")}
      showBackButton={false}
      // Altezza piena: la lista riempie lo spazio rimasto e la paginazione resta in fondo.
      contentClassName="h-full flex flex-col min-h-0 py-2 md:py-4"
      headerAction={
        canCreateItinerary ? (
          <Button size="lg" className="h-8 px-3" onClick={() => setIsCreateItineraryModalOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t("dashboard.createItinerary")}
          </Button>
        ) : undefined
      }
      headerActionCompact={
        canCreateItinerary ? (
          <Button
            size="sm"
            className="h-8 flex-shrink-0 whitespace-nowrap rounded-full px-2.5 text-xs"
            onClick={() => setIsCreateItineraryModalOpen(true)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t("dashboard.createItinerary")}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-1 flex-col min-h-0 gap-6">
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        <div className="flex flex-1 flex-col min-h-0 w-full xl:px-4 md:px-4 mx-auto">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="flex flex-1 flex-col min-h-0 w-full"
          >
            {/* Radix lascia nel DOM anche i pannelli inattivi, nascosti con l'attributo `hidden`:
                la classe `flex` lo scavalcherebbe e i pannelli vuoti si dividerebbero
                l'altezza con quello attivo: da qui data-[state=inactive]:hidden. */}
            {itineraryTabConfigs.map((cfg) => (
              <ProfileTabsContent
                key={cfg.value}
                value={cfg.value}
                className="flex flex-1 flex-col min-h-0 data-[state=inactive]:hidden"
              >
                <DashboardItinerariesTab
                  config={cfg}
                  userRole={userRole}
                  tabTotal={countOf(cfg.value)}
                  refreshKey={refreshKey}
                  onRefresh={refreshData}
                  onEdit={handleEditItinerary}
                  onDelete={handleDeleteItinerary}
                  onDuplicate={handleDuplicateItinerary}
                  onViewTravelRequest={handleViewTravelRequest}
                  onUpdateStatus={handleUpdateItineraryStatus}
                />
              </ProfileTabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      {/* Dettaglio della richiesta di un itinerario, in sola lettura: le azioni sulla
          richiesta stanno nella sua pagina. */}
      <TravelRequestModal
        isOpen={!!viewedRequest}
        onClose={() => setViewRequestId(null)}
        travelRequest={viewedRequest}
        showActions={false}
        isAdmin={userRole === "admin"}
        enableDetailSheets={userRole !== "traveler"}
      />

      <CreateItineraryFromRequestModal
        isOpen={isCreateItineraryModalOpen}
        onClose={() => setIsCreateItineraryModalOpen(false)}
        onConfirm={handleCreateItineraryFromRequest}
      />
    </PrivateLayout>
  );
};

export default Dashboard;